import type {
  IFootTrafficDwellReport,
  IFootTrafficFreqReport,
  IFootTrafficHourReport,
  IFootTrafficTradeAreaBands,
  IFootTrafficWeekdayReport,
} from '@/Interfaces/Attribution/FootTraffic.interface';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import {
  DWELL_LONG_MIN,
  DWELL_SHORT_MAX,
  LIFT_STRONG,
  REPEAT_VISITOR_HIGH,
  REPEAT_VISITOR_LOW,
  YOY_BANDS,
} from './thresholds';

export interface IExecutiveSummaryInput {
  totalVisits: number;
  yoyPercentChange: number | null;
  liftPercent: number | null;
  avgWeeklyUniqueVisitors: number;
  repeatVisitorPercent: number;
}

const fmt = (n: number) => numberToUsFormat(n, { maximumFractionDigits: 0 });

export const getExecutiveSummary = ({
  totalVisits,
  yoyPercentChange,
  liftPercent,
  avgWeeklyUniqueVisitors,
  repeatVisitorPercent,
}: IExecutiveSummaryInput): string => {
  if (!totalVisits) return 'No visits recorded in the selected period.';

  const parts: string[] = [];

  if (yoyPercentChange != null) {
    const abs = Math.abs(yoyPercentChange);
    const dir = yoyPercentChange >= 0 ? 'up' : 'down';
    if (abs >= YOY_BANDS.strong) parts.push(`Visits are ${dir} ${abs.toFixed(0)}% year-over-year`);
    else if (abs >= YOY_BANDS.mild) parts.push(`Visits are slightly ${dir} ${abs.toFixed(0)}% year-over-year`);
    else parts.push('Visits are roughly flat year-over-year');
  } else {
    parts.push(`${fmt(totalVisits)} visits in the selected period`);
  }

  if (repeatVisitorPercent >= REPEAT_VISITOR_HIGH) {
    parts.push(`driven by a strong repeat-visitor base (${(repeatVisitorPercent * 100).toFixed(0)}% return)`);
  } else if (avgWeeklyUniqueVisitors > 0) {
    parts.push(`averaging ${fmt(avgWeeklyUniqueVisitors)} unique visitors a week`);
  }

  if (liftPercent != null && Math.abs(liftPercent) >= LIFT_STRONG) {
    const dir = liftPercent >= 0 ? 'higher' : 'lower';
    parts.push(`with on-air locations ${Math.abs(liftPercent).toFixed(0)}% ${dir} than off-air`);
  }

  return `${parts.join(', ')}.`;
};

const pickDominantSegment = (segments: Record<string, number>): string | null => {
  const entries = Object.entries(segments || {});
  if (!entries.length) return null;
  const [name] = entries.reduce((a, b) => (b[1] > a[1] ? b : a));
  return name;
};

export const getDwellInsight = (report: IFootTrafficDwellReport | null | undefined): string => {
  if (!report || !report.medianDwellTime) return '';
  const median = report.medianDwellTime;
  const dominant = pickDominantSegment(report.segments);
  const tail = dominant ? `, with most visits in the ${dominant} bucket` : '';
  if (median >= DWELL_LONG_MIN) {
    return `Median dwell is ${median.toFixed(1)} min — visitors are spending substantial time on premises${tail}.`;
  }
  if (median <= DWELL_SHORT_MAX) {
    return `Median dwell is ${median.toFixed(1)} min — visits are short${tail}.`;
  }
  return `Median dwell is ${median.toFixed(1)} min${tail}.`;
};

export const getLoyaltyInsight = (report: IFootTrafficFreqReport | null | undefined): string => {
  if (!report || !report.avgWeeklyUniqueVisitors) return '';
  const repeatPct = (report.repeatVisitorPercent * 100).toFixed(0);
  const unique = fmt(report.avgWeeklyUniqueVisitors);
  if (report.repeatVisitorPercent >= REPEAT_VISITOR_HIGH) {
    return `${repeatPct}% of ${unique} weekly unique visitors returned — strong loyalty signal.`;
  }
  if (report.repeatVisitorPercent <= REPEAT_VISITOR_LOW) {
    return `Most of ${unique} weekly unique visitors came once — low return rate (${repeatPct}%).`;
  }
  return `${unique} weekly unique visitors with a ${repeatPct}% return rate.`;
};

const peakInsight = (entries: [string, number][], scope: string): string => {
  if (entries.length < 2) return '';
  const total = entries.reduce((acc, [, v]) => acc + v, 0);
  if (!total) return '';
  const [peakKey, peakVisits] = entries.reduce((a, b) => (b[1] > a[1] ? b : a));
  const share = ((peakVisits / total) * 100).toFixed(0);
  return `${peakKey} is the strongest ${scope}, capturing ${share}% of visits.`;
};

export const getPeakDayInsight = (report: IFootTrafficWeekdayReport | null | undefined): string => {
  const entries = Object.entries(report || {}).filter(([, v]) => v > 0);
  return peakInsight(entries, 'day');
};

export const getPeakHourInsight = (report: IFootTrafficHourReport | null | undefined): string => {
  const entries = Object.entries(report || {}).filter(([, v]) => v > 0) as [string, number][];
  return peakInsight(entries, 'hour');
};

export const getTradeAreaInsight = (
  bands: IFootTrafficTradeAreaBands | null | undefined,
  side: 'home' | 'work' = 'home',
): string => {
  if (!bands || bands.primaryRadiusMiles == null || bands.primaryRadiusPercent == null) return '';
  const pct = Math.round(bands.primaryRadiusPercent * 100);
  const miles = bands.primaryRadiusMiles;
  const milesLabel = miles >= 1 ? `${miles} mi` : `${miles.toFixed(1)} mi`;
  const origin = side === 'home' ? 'live' : 'work';
  return `${pct}% of measured visitors ${origin} within ${milesLabel} of the location — focus geo-targeting on this radius.`;
};
